import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useProponerPlanZoom,
  useZoomPlan,
  type ZoomPlanAccion,
} from "@/hooks/useZoom";

// Pestaña "Plan vs Zoom": cómo DEBE quedar Zoom (data/zoom_plan_deseado.json)
// contra la última foto. Sólo lectura; "Proponer" crea filas en Aprobaciones y
// nada cambia en Zoom hasta que alguien aprueba cada una.

const ESTADO_CAMBIO: Record<string, string> = {
  aplicado: "text-emerald-700",
  ya_estaba: "text-muted-foreground",
  escritura_apagada: "text-amber-600",
  no_aplicado: "text-amber-600",
  no_confirmado: "text-red-600",
  falta_scope: "text-red-600",
  error: "text-red-600",
};

function Accion({ a }: { a: ZoomPlanAccion }) {
  return (
    <li className="text-sm">
      {a.detalle}{" "}
      {a.aprobacion_pendiente ? (
        <Badge variant="secondary">en Aprobaciones</Badge>
      ) : a.propuesta ? (
        <Badge>por proponer</Badge>
      ) : (
        <span className="text-xs text-muted-foreground">({a.bloqueo})</span>
      )}
    </li>
  );
}

export function ZoomPlanTab() {
  const q = useZoomPlan();
  const proponer = useProponerPlanZoom();

  if (q.isPending)
    return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (q.isError)
    return (
      <p className="text-sm text-red-600">No se pudo leer: {String(q.error)}</p>
    );
  const d = q.data;
  const porProponer = d.entradas
    .flatMap((e) => e.acciones)
    .filter((a) => a.propuesta && !a.aprobacion_pendiente).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1 text-sm">
          <p>
            Plan {d.plan.actualizado ?? ""}{" "}
            <span className="font-mono text-xs">{d.plan.huella}</span>
            {d.borrador && (
              <Badge variant="destructive" className="ml-2">
                borrador: no propone
              </Badge>
            )}
            {" · "}
            {d.foto
              ? `foto del ${new Date(d.foto.tomada_en).toLocaleString("es-MX")}`
              : "sin foto de Zoom todavía"}
            {" · "}escritura en Zoom <b>{d.escritura}</b>
          </p>
          <p className="text-muted-foreground">
            {d.resumen.acciones} diferencias · {d.resumen.proponibles}{" "}
            proponibles · {d.resumen.bloqueadas} en espera ·{" "}
            {d.resumen.esperando_portabilidad} números esperando portabilidad ·{" "}
            {d.resumen.falta_usuario} extensiones sin usuario/licencia
          </p>
        </div>
        <Button
          size="sm"
          disabled={proponer.isPending || porProponer === 0 || !d.foto}
          onClick={() => proponer.mutate()}
        >
          {proponer.isPending
            ? "Proponiendo…"
            : `Proponer ${porProponer} en Aprobaciones`}
        </Button>
      </div>
      {proponer.data && (
        <p className="text-sm">
          {proponer.data.motivo ??
            `${proponer.data.creadas} propuestas nuevas en Aprobaciones (${proponer.data.ya_pendientes} ya estaban).`}
        </p>
      )}
      {proponer.isError && (
        <p className="text-sm text-red-600">{String(proponer.error)}</p>
      )}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {d.entradas.map((e) => (
          <Card key={e.clave}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {e.nombre}{" "}
                {e.estado === "pendiente_confirmar" && (
                  <Badge variant="outline">pendiente de confirmar</Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {e.tipo === "cola" ? "Cola" : "Recepcionista"} · en Zoom:{" "}
                {e.en_zoom ?? "no existe"}
                {e.asterisk && <> · Asterisk: {e.asterisk}</>}
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {e.acciones.length === 0 ? (
                <p className="text-sm text-emerald-700">Igual que el plan.</p>
              ) : (
                <ul className="list-disc space-y-1 pl-5">
                  {e.acciones.map((a) => (
                    <Accion key={a.firma} a={a} />
                  ))}
                </ul>
              )}
              {e.avisos.length > 0 && (
                <ul className="space-y-0.5 text-xs text-muted-foreground">
                  {e.avisos.map((v) => (
                    <li key={v.texto}>• {v.texto}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Cambios intentados en Zoom ({d.cambios.length})
        </h3>
        {d.cambios.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ninguno todavía.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {d.cambios.map((c) => (
              <li key={c.id}>
                {new Date(c.creado_en).toLocaleString("es-MX")} ·{" "}
                <b className={ESTADO_CAMBIO[c.estado] ?? ""}>{c.estado}</b> ·{" "}
                {c.detalle}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
